/*
 * animate
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

window.gsap = gsap;

const animate = {
    cfg: {
        name: 'animate',
        selectors: {
            root: '.js-animate',
            svgPath: '.js-path',
            image: '.js-animate-image',
            parallaxItem: '.js-parallaxItem',
        },
        classes: {},
        el: [
            // {
            //     targets: 'body > header > div',
            //     params: { animation: 'fade-up' },
            // },
            // {
            //     targets: 'body > main > div:not([class*=abm], [class*=-hq], .has-bg)',
            //     params: { animation: 'fade-up' },
            // },
            // {
            //     targets: ['.has-bg:not(.has-bgContainer)', '& > div'],
            //     params: { animation: 'fade-up' },
            // },
        ],
        gsapDefaults: {
            duration: 0.6,
            stagger: 0.05,
            ease: 'power3.out',
            delay: 0,
        },
    },

    setAnimation(root, targets, params) {
        const setTimeline = (trigger) =>
            gsap.timeline({
                scrollTrigger: {
                    trigger,
                    ...(params && params.animation === 'fade-up' ? {
                        start: 'top 200%',
                        fastScrollEnd: true, // Optimize for fast scrolling
                        toggleActions: "play none none none",
                        once: true,
                    } : {
                        // when the top of the container hits the bottom of the viewport
                        start: 'top bottom',
                    }),
                },
            });

        let timeline = root ? setTimeline(root) : undefined;

        if (params != null) {
            params = this.getStructuredParams(params);
        }

        for (const element of targets) {
            if (!root) {
                timeline = setTimeline(element);
            }

            const { animation, order, gsapDefaults } =
                params ?? this.getStructuredParams(this.getElParams(element));

            switch (animation) {
                // default animations
                case 'none':
                    break;
                case 'fade-up':
                    {
                        gsap.set(element, { yPercent: 25, opacity: 0 });
                        timeline.to(
                            element,
                            {
                                ...gsapDefaults,
                                yPercent: 0,
                                opacity: 1,
                            },
                            order,
                        );
                    }
                    break;
                case 'zoom':
                    {
                        const zoomImage = element.querySelector(
                            this.cfg.selectors.image,
                        );
                        timeline.to(
                            zoomImage,
                            {
                                ...gsapDefaults,
                                scale: 1.15,
                                delay: 0.5,
                                duration: 5,
                                ease: 'none',
                            },
                            order,
                        );
                    }
                    break;
                case 'parallax':
                    {
                        const parallaxImage = element.querySelector(
                            this.cfg.selectors.image,
                        );
                        timeline.to(
                            parallaxImage,
                            {
                                ease: 'none',
                                yPercent: -20,
                                scrollTrigger: {
                                    trigger: element,
                                    start: 'top bottom',
                                    scrub: true,
                                    //markers: true,
                                    end: () =>
                                        `+=${(root ?? element).offsetHeight} top`,
                                    onEnter: () => {
                                        ScrollTrigger.refresh();
                                    },
                                },
                            },
                            order,
                        );
                    }
                    break;
                //custom animation
                case 'parallax-image-divider':
                    {
                        // refresh the scroll trigger to calculate the start point correctly.
                        ScrollTrigger.refresh();
                        // for each element with 'data-offset' attribute
                        gsap.utils
                            .toArray(this.cfg.selectors.parallaxItem, element)
                            .forEach((element) => {
                                // speed of parallax effect
                                const offset =
                                    parseFloat(
                                        element.getAttribute('data-offset'),
                                    ) || 0.5;

                                // calculate Scroll
                                const positiveValue =
                                    offset * element.offsetHeight; //(1 - speed) * (maxScroll - adjustedScrollStart);

                                // Add tweens
                                gsap.set(element, { y: positiveValue }); // set positve y value
                                timeline.to(
                                    element,
                                    {
                                        ease: 'none',
                                        y: 0, // calculated negative y value
                                        scrollTrigger: {
                                            // scrollTrigger configuration
                                            trigger: element,
                                            start: 'top 100%',
                                            scrub: 1.2,
                                            // markers: true, // Show markers for debugging
                                            end: () =>
                                                `+=${window.innerHeight - 70}px center`,
                                            onEnter: ScrollTrigger.refresh(),
                                        },
                                    },
                                    order,
                                );
                            });
                    }
                    break;
                default:
                    console.error('Unknown animation type:', animation);
                    break;
            }
        }
    },

    initAnimations() {
        gsap.registerPlugin(ScrollTrigger);

        for (const target of document.querySelectorAll(
            this.cfg.selectors.root,
        )) {
            const params = this.getElParams(target);
            if (params?.animation) {
                this.setAnimation(target, [target]);
                continue;
            }
            this.setAnimation(
                target,
                target.querySelectorAll('[data-animation]'),
            );
        }

        for (const { targets, params } of this.cfg.el) {
            if (Array.isArray(targets)) {
                const [rootSelector, ...children] = targets;
                for (const root of document.querySelectorAll(rootSelector)) {
                    for (let i = 0; i < children.length; i++) {
                        const elements = [...root.querySelectorAll(children[i])];
                        if (!params.order) {
                            params.order = i;
                        }
                        this.setAnimation(root, elements, params);
                    }
                }
                continue;
            }

            // get all elements which are not of type .js-animate
            const elements = [...document.querySelectorAll(targets)].filter(
                (el) =>
                    !el.className.includes(
                        this.cfg.selectors.root.slice(1),
                    ),
            );

            this.setAnimation(null, elements, params);
        }
    },

    getElParams(el) {
        return Object.fromEntries(
            ['order', 'duration', 'stagger', 'ease', 'delay']
                .map((key) => [key, el.getAttribute(`data-animation-${key}`)])
                .concat([['animation', el.getAttribute('data-animation')]]),
        );
    },

    getStructuredParams(params) {
        const order = this.setDefault(params.order, 0, Number.parseInt);

        // default options
        const gsapDefaults = {
            duration: this.setDefault(
                params.duration,
                this.cfg.gsapDefaults.duration,
                Number.parseFloat,
            ),
            stagger: this.setDefault(
                params.stagger,
                this.cfg.gsapDefaults.stagger,
                Number.parseFloat,
            ),
            delay: this.setDefault(
                params.delay,
                this.cfg.gsapDefaults.delay,
                Number.parseFloat,
            ),
            ease: this.setDefault(params.ease, this.cfg.gsapDefaults.ease),
        };

        return { animation: params.animation, order, gsapDefaults };
    },

    setDefault(value, defValue, transform) {
        if (value == null) {
            return defValue;
        }
        return typeof transform === 'function' ? transform(value) : value;
    },

    init() {
        console.debug(`Init: ${this.cfg.name}`);
        this.initAnimations();
    },
};

export default animate;
